import { useEffects } from '@/contexts/EffectsContext';
import { useGame } from '@/contexts/GameContext';
import { useSound } from '@/contexts/SoundContext';
import { useShop } from '@/contexts/ShopContext';
import { puzzles } from '@/game/puzzles';
import { logger } from '@/services/logging';
import type { Direction, GameSettings, GameState, GameStats, MoveSnapshot, Position } from '@/types/game';
import type { EnemyState } from '@/types/enemy';
import type { CardType } from '@/types/card';
import type { PuzzleData } from '@/types/puzzle';
import { getPuzzleGrid, getPuzzleCollectibles, getPuzzleMetadata } from '@/types/puzzle';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LevelUtils } from '@/game/levelUtils';
import { getPropEffectsManager } from '@/game/propEffects';
import { getCommentarySystem } from '@/game/commentarySystem';
import { getUpgradeManager } from '@/game/upgrades';
import { buildTeleporterMap, getTeleporterDestination, type TeleporterMap } from '@/game/teleporters';
import { resolveLevelCollectibles } from '@/game/levelCollectibles';
import { WORLD_ENEMY_MAP, ENEMY_DEFINITIONS } from '@/config/enemies';
import { getWorldForLevel } from '@/config/worlds';
import { useEnemyEngine } from '@/hooks/useEnemyEngine';
import { deterministicDirection, computeActivationDelay } from '@/game/enemyAI';
import { ENEMY_DIFFICULTY, ENEMY_BASE_DELAY, ENEMY_STAGGER } from '@/config/enemyDifficulty';
import { pickInfiniteLevel, waveToTier } from '@/data/infiniteLevels/index';
import type { InfiniteState } from '@/types/game';

// -- Infinite run persistence ------------------------------------------------

const INFINITE_RUN_KEY = 'infiniteRunState';

/** Persist the full infinite run state so the player can resume after refresh / navigation. */
export function saveInfiniteRunState(state: InfiniteState): void {
  try {
    localStorage.setItem(INFINITE_RUN_KEY, JSON.stringify(state));
  } catch { /* quota exceeded – non-critical */ }
}

/** Retrieve saved run state, or null if none exists. */
export function getSavedInfiniteRunState(): InfiniteState | null {
  try {
    const raw = localStorage.getItem(INFINITE_RUN_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as InfiniteState;
  } catch {
    return null;
  }
}

/** Clear saved run (used when starting a fresh run or game over). */
export function clearInfiniteRunState(): void {
  localStorage.removeItem(INFINITE_RUN_KEY);
}

const GAME_WIDTH = 29;

/**
 * Handles the case where an enemy slides into the player's tile.
 * Sets collision flag so the existing life-loss / reset logic picks it up.
 */
function handleEnemyCollisionInState(
  state: GameState,
  newEnemies: EnemyState[],
  _collider: EnemyState,
): GameState {
  return {
    ...state,
    enemies: newEnemies,
    hasCollided: true,
  };
}

export const useGameEngine = () => {
  const effects = useEffects();
  const { playLevelStart, playLevelComplete, playHitObstacle, playWallHit, playIceSlide, playFireHit, playFreeze, playWarp, playMove, playTeleport, playCollectCoin, playAchievement, playLevelFailed, playLevelTransition, playEnemyMove, playEnemyCollision, playHurt, playShieldAbsorb, startWorldMusic, stopWorldMusic } = useSound();
  const { updateGameState: updateContextGameState } = useGame();
  const { hasShield, hasInvincibility, shopState, getEquippedPropEffects, shopManager } = useShop();
  const { moveAllEnemiesAsync, checkCollision } = useEnemyEngine();

  // Ref to always have latest shopState available in callbacks (avoids stale closures)
  const shopStateRef = useRef(shopState);
  shopStateRef.current = shopState;

  // Game state
  const [gameState, setGameState] = useState<GameState>({
    level: 0,
    position: [0, 0],
    initialPosition: [0, 0],
    end: [0, 0],
    rocks: [],
    width: GAME_WIDTH,
    isPlaying: false,
    isPaused: false,
    hasCollided: false,
    lastMovementDirection: null,
    collectibles: [],
    collectedInLevel: 0,
    gameStats: {
      levelStartTime: Date.now(),
      stepsTaken: 0,
      timeSpent: 0,
      rocksEncountered: 0,
      lavaEncountered: 0,
      stopsEncountered: 0,
      keyPressSpeed: 0,
      retries: 0,
      score: 0,
      streak: 0,
      perfectLevels: 0,
    },
    gameSettings: {
      graphics: (localStorage.getItem('graphics') as 'high' | 'medium' | 'low') || 'high',
      effects: localStorage.getItem('effects') !== 'false',
      sound: localStorage.getItem('sound') !== 'false',
      vibration: localStorage.getItem('vibration') !== 'false',
      theme: (localStorage.getItem('theme') as 'default' | 'dark' | 'light' | 'retro') || 'default',
    },
    totalScore: parseInt(localStorage.getItem('totalScore') || '0'),
    achievements: JSON.parse(localStorage.getItem('achievements') || '[]'),
    gameMode: (localStorage.getItem('gameMode') as 'standard' | 'timed' | 'endless') || 'standard',
    completedLevels: JSON.parse(localStorage.getItem('completedLevels') || '[]'),
    levelStats: JSON.parse(localStorage.getItem('levelStats') || '{}'),
    enemies: [],
    hand: [],
    moveHistory: [],
    frozenTurns: 0,
    scoutMovesLeft: 0,
    isWarpSelecting: false,
  });
  
  // Refs to keep latest values accessible in callbacks without stale closures
  const completedLevelsRef = useRef(gameState.completedLevels);
  completedLevelsRef.current = gameState.completedLevels;

  // Track which collectibles have already been credited to the shop so we never double-award.
  const creditedCollectiblesRef = useRef<Set<string>>(new Set());
  const prevLavaEncounteredRef = useRef(0);

  const teleporterMapRef = useRef<TeleporterMap>(new Map());

  // Current puzzle grid -- for standard levels this mirrors puzzles[level],
  // for infinite mode it stores the generated grid.
  const currentGridRef = useRef<string[]>([]);

  // Timeout IDs for staggered enemy movement so we can clean them up on
  // unmount or when state changes mid-animation.
  const enemyStaggerTimeoutsRef = useRef<number[]>([]);

  const getPassiveEffects = useCallback(() => {
    const propEffects = getEquippedPropEffects();
    const upgradeEffects = getUpgradeManager().getPropEffects();
    return [...propEffects, ...upgradeEffects];
  }, [getEquippedPropEffects]);

  // Hint path state
  const [hintPath, setHintPath] = useState<Position[] | null>(null);
  // Set of "col,row" keys for hint-path tiles that overlap with enemy positions
  const [hintPathHazards, setHintPathHazards] = useState<Set<string>>(new Set());

  // Collision timer ref
  const collisionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Death/game-over timer refs (for cleanup on unmount)
  const deathTimerRef = useRef<NodeJS.Timeout | null>(null);
  const gameOverTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Commentary system integration
  useEffect(() => {
    if (gameState.isPlaying) {
      const commentarySystem = getCommentarySystem();
      commentarySystem.analyzeGameState(gameState, gameState.gameStats);
    }
  }, [gameState]);

  // Use hint powerup (or scout card) for showing path
  useEffect(() => {
    const shouldShowHint = (shopState.powerups.hint > 0 || gameState.scoutMovesLeft > 0) && gameState.isPlaying;
    if (shouldShowHint) {
      // Calculate and show hint path using the stored grid
      const levelUtils = new LevelUtils();
      const puzzleGrid = currentGridRef.current;
      if (puzzleGrid && puzzleGrid.length > 0) {
        const puzzleString = puzzleGrid.join('');
        const optimalPath = levelUtils.findOptimalPath(puzzleString, gameState.width);
        if (optimalPath) {
          // Convert path to match current position format (they're already in [col, row] format)
          const currentPosIndex = optimalPath.findIndex(
            pos => pos[0] === gameState.position[0] && pos[1] === gameState.position[1]
          );
          if (currentPosIndex !== -1) {
            const pathFromCurrent = optimalPath.slice(currentPosIndex);
            setHintPath(pathFromCurrent);

            // Build a set of enemy positions for hazard detection on the path.
            // Mark path tiles that have enemies on them or adjacent to them (1 tile)
            // so the UI can render hazard warnings.
            const enemies = gameState.enemies || [];
            const enemyKeys = new Set<string>();
            for (const enemy of enemies) {
              if (enemy.isStunned) continue;
              const [ex, ey] = enemy.position;
              // Mark the enemy tile and all adjacent tiles as hazardous zones
              enemyKeys.add(`${ex},${ey}`);
              enemyKeys.add(`${ex - 1},${ey}`);
              enemyKeys.add(`${ex + 1},${ey}`);
              enemyKeys.add(`${ex},${ey - 1}`);
              enemyKeys.add(`${ex},${ey + 1}`);
            }

            const hazards = new Set<string>();
            for (const pos of pathFromCurrent) {
              const key = `${pos[0]},${pos[1]}`;
              if (enemyKeys.has(key)) {
                hazards.add(key);
              }
            }
            setHintPathHazards(hazards);
          }
        }
      }
    } else {
      setHintPath(null);
      setHintPathHazards(new Set());
    }
  }, [shopState.powerups.hint, gameState.scoutMovesLeft, gameState.isPlaying, gameState.level, gameState.position, gameState.enemies]);

  // Timer state
  const [timer, setTimer] = useState({ minutes: 0, seconds: 0 });
  const [complexity, setComplexity] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer functions
  const startTimer = useCallback((levelIndex: number) => {
    setTimer({ minutes: 0, seconds: 0 });
    
    // Calculate complexity using puzzle metadata or fallback
    const puzzle = puzzles[levelIndex] as PuzzleData;
    if (puzzle) {
      const puzzleMeta = getPuzzleMetadata(puzzle, levelIndex);
      const puzzleGrid = getPuzzleGrid(puzzle);
      const fallbackComplexity = puzzleGrid.join('').length;
      setComplexity(puzzleMeta.stepTarget || fallbackComplexity);
    } else {
      setComplexity(0);
    }

    timerRef.current = setInterval(() => {
      setTimer(prev => {
        // slowTime powerup halves timer speed (tick every 2s instead of 1s)
        if (shopStateRef.current.powerups.slowTime > 0) {
          // Skip every other tick to simulate half speed
          if ((prev.seconds + prev.minutes * 60) % 2 !== 0) {
            return prev;
          }
        }
        if (prev.seconds >= 59) {
          return { minutes: prev.minutes + 1, seconds: 0 };
        }
        return { ...prev, seconds: prev.seconds + 1 };
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimer({ minutes: 0, seconds: 0 });
  }, [stopTimer]);

  // Pause controls
  const togglePause = useCallback(() => {
    setGameState(prev => ({ ...prev, isPaused: !prev.isPaused }));
  }, []);

  const setPaused = useCallback((paused: boolean) => {
    setGameState(prev => ({ ...prev, isPaused: paused }));
  }, []);

  // Game statistics
  const updateSettings = useCallback((newSettings: Partial<GameSettings>) => {
    setGameState(prev => ({
      ...prev,
      gameSettings: { ...prev.gameSettings, ...newSettings }
    }));
  }, []);

  const resetGameStats = useCallback((exclude?: keyof GameStats) => {
    setGameState(prev => ({
      ...prev,
      gameStats: {
        ...prev.gameStats,
        levelStartTime: exclude === 'levelStartTime' ? prev.gameStats.levelStartTime : Date.now(),
        stepsTaken: exclude === 'stepsTaken' ? prev.gameStats.stepsTaken : 0,
        timeSpent: exclude === 'timeSpent' ? prev.gameStats.timeSpent : 0,
        rocksEncountered: exclude === 'rocksEncountered' ? prev.gameStats.rocksEncountered : 0,
        lavaEncountered: exclude === 'lavaEncountered' ? prev.gameStats.lavaEncountered : 0,
        stopsEncountered: exclude === 'stopsEncountered' ? prev.gameStats.stopsEncountered : 0,
        keyPressSpeed: exclude === 'keyPressSpeed' ? prev.gameStats.keyPressSpeed : 0,
        retries: exclude === 'retries' ? prev.gameStats.retries : 0,
        score: exclude === 'score' ? prev.gameStats.score : 0,
        streak: exclude === 'streak' ? prev.gameStats.streak : 0,
        perfectLevels: exclude === 'perfectLevels' ? prev.gameStats.perfectLevels : 0,
      }
    }));
  }, []);

  // Note: Special encounters are now handled inline in movePlayerInDirection for better performance

  // Store level completion data for when animations finish
  const [pendingLevelComplete, setPendingLevelComplete] = useState<{
    level: number;
    score: number;
    stars: number;
    completionTime: number;
    steps: number;
    retries: number;
    keyPressSpeed: number;
    nextLevel: number | null;
    collectiblesCollected: number;
    collectiblesTotal: number;
  } | null>(null);
  
  // Prevent multiple level complete calls
  const [levelCompleteTriggered, setLevelCompleteTriggered] = useState(false);
  const levelCompleteTriggeredRef = useRef(false);
  
  // Restart counter to force camera reset
  const [restartCounter, setRestartCounter] = useState(0);

  // Keep refs in sync when we change levels or restart.
  useEffect(() => {
    creditedCollectiblesRef.current = new Set();
    prevLavaEncounteredRef.current = gameState.gameStats.lavaEncountered;
  }, [gameState.level, restartCounter]);

  // Apply shop currency updates *after* React commits game state changes.
  // Doing this inside setGameState's render-phase updater causes "setState during render" warnings.
  useEffect(() => {
    const credited = creditedCollectiblesRef.current;
    const newlyCollected = gameState.collectibles.filter(c => c.isCollected && !credited.has(c.id));
    if (newlyCollected.length === 0) return;

    newlyCollected.forEach(c => credited.add(c.id));

    const propManager = getPropEffectsManager();
    const passiveEffects = getPassiveEffects();
    const luckMultiplier = propManager.getCoinMultiplier(passiveEffects);

    let deltaCoins = 0;
    let deltaGems = 0;
    for (const c of newlyCollected) {
      const finalValue = Math.floor(c.value * luckMultiplier);
      if (finalValue <= 0) continue;
      if (c.type === 'gem') deltaGems += finalValue;
      else deltaCoins += finalValue;
    }

    if (deltaCoins > 0) shopManager.addCoins(deltaCoins);
    if (deltaGems > 0) shopManager.addGems(deltaGems);
  }, [gameState.collectibles, getPassiveEffects, shopManager]);

  // Apply life loss after commit based on lava encounters (only increments when damage is taken).
  useEffect(() => {
    const prev = prevLavaEncounteredRef.current;
    const current = gameState.gameStats.lavaEncountered;
    prevLavaEncounteredRef.current = current;

    const delta = current - prev;
    if (delta <= 0) return;

    shopManager.setLives(Math.max(0, shopStateRef.current.lives - delta));
  }, [gameState.gameStats.lavaEncountered, shopManager]);

  // Move enemies after every player step, with staggered visual updates.
  //
  // All enemy AI is computed at once (via the worker for performance) but the
  // resulting positions are applied to state one-by-one with a short delay
  // between each enemy so the player can visually track individual movements.
  useEffect(() => {
    if (!gameState.isPlaying || gameState.isPaused) return;
    if (gameState.enemies.length === 0) return;
    if (gameState.gameStats.stepsTaken === 0) return; // Don't move on initial load

    // If enemies are frozen (Freeze card), just decrement
    if (gameState.frozenTurns > 0) {
      setGameState(prev => ({ ...prev, frozenTurns: prev.frozenTurns - 1 }));
      return;
    }

    // Get the current puzzle grid for sliding physics
    const grid = currentGridRef.current;
    if (!grid || grid.length === 0) return;

    // Clean up any in-flight stagger timeouts from a previous turn
    const staggerIds = enemyStaggerTimeoutsRef.current;
    staggerIds.forEach((tid) => clearTimeout(tid));
    enemyStaggerTimeoutsRef.current = [];

    const timerId = window.setTimeout(async () => {
      // Capture state from the effect's closure for the async worker call
      const playerPrevPos: Position =
        gameState.moveHistory.length > 0
          ? gameState.moveHistory[gameState.moveHistory.length - 1].playerPosition
          : gameState.initialPosition;

      const newEnemies = await moveAllEnemiesAsync(
        gameState.enemies,
        grid,
        gameState.width,
        gameState.position,
        playerPrevPos,
        gameState.lastMovementDirection ?? null,
        gameState.collectibles,
      );

      // Apply each enemy's new position with a staggered delay so the player
      // can see enemies move one at a time rather than all at once.
      const enemyCount = newEnemies.length;
      for (let i = 0; i < enemyCount; i++) {
        const delay = i * ENEMY_STAGGER;
        const capturedEnemy = newEnemies[i];
        const capturedIndex = i;
        const isLast = i === enemyCount - 1;

        const sid = window.setTimeout(() => {
          setGameState(prev => {
            // Guard: if game state changed while waiting (paused, stopped, etc.), skip
            if (!prev.isPlaying || prev.isPaused) return prev;

            // Replace this single enemy in the array
            const updatedEnemies = prev.enemies.map((e, idx) =>
              idx === capturedIndex ? capturedEnemy : e,
            );

            // Play enemy move sound if this enemy actually moved
            const prevEnemy = prev.enemies[capturedIndex];
            if (
              prevEnemy &&
              (capturedEnemy.position[0] !== prevEnemy.position[0] ||
               capturedEnemy.position[1] !== prevEnemy.position[1])
            ) {
              playEnemyMove();
            }

            // Check collision after applying this enemy
            const collider = checkCollision(prev.position, updatedEnemies);
            if (collider) {
              playEnemyCollision();
              playHurt();
              return handleEnemyCollisionInState(prev, updatedEnemies, collider);
            }

            return { ...prev, enemies: updatedEnemies };
          });
        }, delay) as unknown as number;

        enemyStaggerTimeoutsRef.current.push(sid);
      }
    }, ENEMY_BASE_DELAY); // Base delay so the player sees their move first

    // Store the initial timer so we can cancel it on cleanup
    enemyStaggerTimeoutsRef.current.push(timerId as unknown as number);

    return () => {
      enemyStaggerTimeoutsRef.current.forEach((tid) => clearTimeout(tid));
      enemyStaggerTimeoutsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.gameStats.stepsTaken, gameState.isPlaying, gameState.isPaused, gameState.enemies.length]);

  const handleLevelComplete = useCallback(() => {
    // Prevent multiple calls for the same level (use ref to avoid stale closure)
    if (levelCompleteTriggeredRef.current) {
      logger.warn('game', 'handleLevelComplete called multiple times, ignoring');
      return;
    }

    levelCompleteTriggeredRef.current = true;
    setLevelCompleteTriggered(true);
    const completionTime = timer.minutes * 60 + timer.seconds;
    logger.info('game', 'Level completed', {
      level: gameState.level,
      time: completionTime,
      steps: gameState.gameStats.stepsTaken,
      retries: gameState.gameStats.retries,
    });

    stopTimer();
    playLevelComplete();
    
    // Trigger victory animations
    if (effects.animateCharacterVictory) {
      effects.animateCharacterVictory();
    }
    
    // Add sparkle effects
    effects.triggerEffect({
      type: 'sparkle',
      position: [50, 50],
      duration: 2000
    });

    // Calculate score and stars with multiplier (from powerups, props, and meta upgrades)
    const passiveEffects = getPassiveEffects();
    const propManager = getPropEffectsManager();
    const propScoreMultiplier = propManager.getScoreMultiplier(passiveEffects);
    const powerupMultiplier = shopState.powerups.doubleCoins > 0 ? 2 : 1;
    const totalMultiplier = powerupMultiplier * propScoreMultiplier;
    
    const timeBonus = Math.max(0, complexity - (timer.minutes * 60 + timer.seconds));
    const stepBonus = Math.max(0, complexity - gameState.gameStats.stepsTaken);
    const baseScore = 100 + timeBonus * 10 + stepBonus * 5;
    const levelScore = Math.floor(baseScore * totalMultiplier);

    // Calculate stars (1-3) based on performance
    let stars = 1; // Base star for completion
    if (gameState.gameStats.stepsTaken <= complexity * 0.5) stars++; // Efficient completion
    if (completionTime <= 60) stars++; // Fast completion

    // Collectible penalty caps the max achievable stars
    const totalCollectibles = gameState.collectibles.length;
    const collectedCount = gameState.collectibles.filter(c => c.isCollected).length;
    if (totalCollectibles > 0) {
      if (collectedCount < totalCollectibles) stars = Math.min(stars, 2);         // miss any -> cap at 2
      if (collectedCount < totalCollectibles * 0.5) stars = Math.min(stars, 1);   // miss >50% -> cap at 1
    }

    // Update completed levels - validate level index is within bounds
    const isValidLevel = gameState.level >= 0 && gameState.level < puzzles.length;
    const newCompletedLevels = (gameState.completedLevels.includes(gameState.level) || !isValidLevel)
      ? gameState.completedLevels
      : [...gameState.completedLevels, gameState.level];
    
    if (!isValidLevel) {
      logger.warn('game', `Attempted to complete invalid level ${gameState.level}, max is ${puzzles.length - 1}`);
    }

    // Update level stats
    const currentLevelStats = gameState.levelStats[gameState.level] || {
      completed: false,
      bestTime: Infinity,
      bestScore: 0,
      totalAttempts: 0,
      stars: 0,
      firstCompletedAt: 0,
      lastPlayedAt: Date.now(),
    };

    const updatedLevelStats = {
      ...currentLevelStats,
      completed: true,
      bestTime: Math.min(currentLevelStats.bestTime, completionTime),
      bestScore: Math.max(currentLevelStats.bestScore, levelScore),
      totalAttempts: currentLevelStats.totalAttempts + 1,
      stars: Math.max(currentLevelStats.stars, stars),
      firstCompletedAt: currentLevelStats.firstCompletedAt || Date.now(),
      lastPlayedAt: Date.now(),
    };

    const newLevelStats = {
      ...gameState.levelStats,
      [gameState.level]: updatedLevelStats,
    };

    // Update total score
    const newTotalScore = gameState.totalScore + levelScore;

    // Award coins for level completion (only for first-time completions)
    const isFirstTimeCompletion = !gameState.completedLevels.includes(gameState.level);
    if (isFirstTimeCompletion) {
      const coinsEarned = Math.floor(levelScore / 100); // Convert score to coins (legacy logic)
      const minCoinsPerLevel = 10; // Ensure minimum reward
      const finalCoinsEarned = Math.max(coinsEarned, minCoinsPerLevel);
      
      logger.info('game', 'Level completion coin reward', {
        level: gameState.level,
        score: levelScore,
        coinsEarned: finalCoinsEarned,
        isFirstTime: true
      });
      
      // Additive update to avoid races with other coin awards (e.g., collectibles).
      shopManager.addCoins(finalCoinsEarned);
    } else {
      logger.info('game', 'Level already completed - no coin reward', {
        level: gameState.level,
        isFirstTime: false
      });
    }

    setGameState(prev => ({
      ...prev,
      isPlaying: false,
      totalScore: newTotalScore,
      completedLevels: newCompletedLevels,
      levelStats: newLevelStats,
      gameStats: {
        ...prev.gameStats,
        timeSpent: completionTime,
        score: levelScore,
      }
    }));

    // Persist to localStorage
    localStorage.setItem('totalScore', newTotalScore.toString());
    localStorage.setItem('completedLevels', JSON.stringify(newCompletedLevels));
    localStorage.setItem('levelStats', JSON.stringify(newLevelStats));
    
    // Also update the GameContext to keep everything in sync
    updateContextGameState({
      completedLevels: newCompletedLevels,
      levelStats: newLevelStats,
      totalScore: newTotalScore,
    });

    // Save game state including level completion
    // Clamp next level to valid range so we never persist an out-of-bounds index
    const savedNextLevel = Math.min(gameState.level + 1, puzzles.length - 1);
    const gameStateToSave = {
      level: savedNextLevel, // Save next level for continue functionality (clamped)
      gameStats: {
        ...gameState.gameStats,
        timeSpent: completionTime,
        score: levelScore,
      },
      position: gameState.position,
      completedLevel: gameState.level,
    };
    localStorage.setItem('gameState', JSON.stringify(gameStateToSave));

    // Show completion effects at the player's position
    const character = document.querySelector('.game__character') as HTMLElement;
    if (character) {
      const rect = character.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth * 100;
      const y = (rect.top + rect.height / 2) / window.innerHeight * 100;
      effects.triggerEffect({ 
        type: 'confetti', 
        position: [x, y],
        duration: 2000 
      });
    } else {
      effects.triggerEffect({ type: 'confetti', duration: 2000 });
    }
    
    // Show achievement notifications for perfect levels
    if (stars === 3 && effects.showAchievement) {
      effects.showAchievement('Perfect Level!', 'trophy');
      playAchievement();
    } else if (gameState.gameStats.retries === 0 && effects.showAchievement) {
      effects.showAchievement('First Try Success!', 'target');
      playAchievement();
    }

    // Store completion data for when camera animations finish
    setPendingLevelComplete({
      level: gameState.level,
      score: levelScore,
      stars,
      completionTime,
      steps: gameState.gameStats.stepsTaken,
      retries: gameState.gameStats.retries,
      keyPressSpeed: gameState.gameStats.keyPressSpeed,
      nextLevel: gameState.level + 1 < puzzles.length ? gameState.level + 1 : null,
      collectiblesCollected: collectedCount,
      collectiblesTotal: totalCollectibles,
    });
  }, [stopTimer, playLevelComplete, playAchievement, timer, complexity, gameState, effects.triggerEffect]);

  // Trigger level completion *after* React commits the move update.
  // Doing this during the setGameState updater (or timers scheduled from it) can cause:
  // "Cannot update a component while rendering a different component" when we update ShopProvider.
  useEffect(() => {
    if (levelCompleteTriggered) return;
    if (!gameState.isPlaying) return;

    // Player can exit without all collectibles -- missing items reduce stars
    if (
      gameState.position[0] !== gameState.end[0] ||
      gameState.position[1] !== gameState.end[1]
    ) {
      return;
    }

    handleLevelComplete();
  }, [
    gameState.end,
    gameState.isPlaying,
    gameState.position,
    handleLevelComplete,
    levelCompleteTriggered,
  ]);
  
  // Function to be called when exit animations are complete
  const triggerLevelCompleteModal = useCallback(() => {
    if (pendingLevelComplete && levelCompleteTriggered) {
      logger.info('game', 'Triggering level complete modal', { level: pendingLevelComplete.level });
      
      // Dispatch level complete event for modal
      window.dispatchEvent(new CustomEvent('levelComplete', { 
        detail: pendingLevelComplete
      }));
      
      // Stop playing but don't load next level yet
      setGameState(prev => ({ ...prev, isPlaying: false }));
    } else {
      logger.warn('game', 'triggerLevelCompleteModal called but level not complete', {
        hasPending: !!pendingLevelComplete,
        isTriggered: levelCompleteTriggered
      });
    }
  }, [pendingLevelComplete, levelCompleteTriggered]);

  // Check for new obstacles in level
  const checkForNewObstacles = useCallback((levelData: string[], levelIndex: number): string | null => {
    const levelString = levelData.join('');
    const obstacles: string[] = [];
    
    // Analyze level for obstacles
    if (levelString.includes('x') || levelString.includes('X')) obstacles.push('rock');
    if (levelString.includes('l') || levelString.includes('L')) obstacles.push('lava');
    if (levelString.includes('s') || levelString.includes('S')) obstacles.push('stop');
    if (levelString.includes('t') || levelString.includes('T')) obstacles.push('teleporter');
    if (levelString.includes('n') || levelString.includes('N')) obstacles.push('enemy');

    // Check which obstacles haven't been shown tutorial for
    for (const obstacle of obstacles) {
      const tutorialKey = `obstacle_tutorial_${obstacle}`;
      const wasShown = localStorage.getItem(tutorialKey);

      // Check if this is the first appearance level for this obstacle
      // firstAppearance values are 0-based (levelIndex)
      const firstAppearance: Record<string, number> = {
        rock: 0,        // Shows on level 1 (index 0)
        lava: 15,       // Shows on level 16 (index 15)
        stop: 11,       // Shows on level 12 (index 11)
        teleporter: 29, // Shows on level 30 (index 29)
        enemy: 5,       // First enemies appear around level 6
      };
      const threshold = firstAppearance[obstacle] ?? 999;
      
      if (!wasShown && levelIndex >= threshold) {
        return obstacle;
      }
    }
    
    return null;
  }, []);
  
  // Load level. When silent=true, skips playLevelStart and startWorldMusic
  // so the caller can defer them until after a loading transition.
  const loadLevel = useCallback((levelIndex: number, options?: { silent?: boolean }) => {
    // Clamp to valid range
    const clampedIndex = Math.max(0, Math.min(levelIndex, puzzles.length - 1));
    if (clampedIndex !== levelIndex) {
      logger.warn('game', `Level ${levelIndex} out of range [0-${puzzles.length - 1}], clamped to ${clampedIndex}`);
    }
    levelIndex = clampedIndex;

    const puzzle = puzzles[levelIndex] as PuzzleData;
    if (!puzzle) {
      logger.error('game', `Failed to load level ${levelIndex} - invalid puzzle data`);
      return false;
    }
    
    // Get puzzle data using helpers
    const puzzleGrid = getPuzzleGrid(puzzle);
    const puzzleCollectibles = getPuzzleCollectibles(puzzle);
    const puzzleMeta = getPuzzleMetadata(puzzle, levelIndex);

    logger.info('game', `Loading level ${levelIndex}`, {
      isTutorial: levelIndex === 0,
      puzzleSize: `${puzzleGrid[0]?.length || 0}x${puzzleGrid.length}`,
      theme: puzzleMeta.theme,
      difficulty: puzzleMeta.difficulty,
      collectibleCount: puzzleCollectibles.length
    });

    const newRocks: Array<[number, number, string]> = []; // [col, row, type]
    const enemySpawns: Position[] = [];
    let newInitialPosition: Position = [0, 0];
    let newEnd: Position = [0, 0];
    const isTutorial = levelIndex === 0;

    // Parse puzzle data exactly like vanilla JS

    puzzleGrid.forEach((row, rowIndex) => {
      const cells = row.split('');
      cells.forEach((cell, cellIndex) => {
        const position: Position = [cellIndex, rowIndex]; // [col, row] format

        switch (cell) {
          case 'x':
          case 'X':
            newRocks.push([cellIndex, rowIndex, 'rock']);
            break;
          case 'o':
          case 'O':
            newInitialPosition = position;
            break;
          case 'e':
          case 'E':
            newEnd = position;
            break;
          case 'l':
          case 'L':
            newRocks.push([cellIndex, rowIndex, 'lava']);
            break;
          case 's':
          case 'S':
            newRocks.push([cellIndex, rowIndex, 'stop']);
            break;
          case 't':
          case 'T':
            newRocks.push([cellIndex, rowIndex, 'teleporter']);
            break;
          case 'n':
          case 'N':
            // Enemy spawn point - treat as stop tile for sliding physics
            newRocks.push([cellIndex, rowIndex, 'stop']);
            enemySpawns.push([cellIndex, rowIndex] as Position);
            break;
        }
      });
    });

    // Create enemy states from spawn points using the world's enemy type.
    // Spawn validation: reject any spawn on the start tile and compute
    // activation delay proportional to Manhattan distance from start.
    const worldDef = getWorldForLevel(levelIndex);
    const enemyType = WORLD_ENEMY_MAP[worldDef.id];
    const difficultyConfig = ENEMY_DIFFICULTY[enemyType];
    const validSpawns = enemySpawns.filter(
      (pos) => pos[0] !== newInitialPosition[0] || pos[1] !== newInitialPosition[1],
    );
    if (validSpawns.length < enemySpawns.length) {
      logger.warn('game', `Rejected ${enemySpawns.length - validSpawns.length} enemy spawn(s) on the start tile`);
    }
    const enemies: EnemyState[] = validSpawns.map((pos, i) => ({
      id: `enemy_${i}`,
      type: enemyType,
      position: pos,
      previousPosition: pos,
      direction: deterministicDirection(pos[0], pos[1], i),
      isStunned: false,
      turnsUntilActive: computeActivationDelay(pos, newInitialPosition, difficultyConfig),
    }));

    // Show "Watch out!" commentary on first enemy encounter per world
    if (enemies.length > 0) {
      const seenEnemyWorlds: string[] = JSON.parse(localStorage.getItem('seenEnemyWorlds') || '[]');
      if (!seenEnemyWorlds.includes(worldDef.id)) {
        seenEnemyWorlds.push(worldDef.id);
        localStorage.setItem('seenEnemyWorlds', JSON.stringify(seenEnemyWorlds));
        const commentary = getCommentarySystem();
        const enemyDef = ENEMY_DEFINITIONS[enemyType];
        commentary.trigger(`Watch out! ${enemyDef.name}s lurk here.`, 'warning', 'high');
      }
    }

    // Build teleporter pairs for this level (used by movement).
    teleporterMapRef.current = buildTeleporterMap(puzzleGrid);

    // Store current grid so enemy AI and movement can access it (needed for infinite mode too)
    currentGridRef.current = puzzleGrid;
    
    // Always spawn collectibles so levels are fully playable on replay.
    const isLevelAlreadyCompleted = completedLevelsRef.current.includes(levelIndex);
    const {
      collectibles: newCollectibles,
      usedFallback: collectibleFallbackUsed,
      reason: collectibleSourceReason,
    } = resolveLevelCollectibles(
      puzzleGrid,
      puzzleCollectibles,
      levelIndex,
      restartCounter,
    );

    logger.info('game', 'Level collectibles', {
      level: levelIndex,
      isCompleted: isLevelAlreadyCompleted,
      collectibleCount: newCollectibles.length,
      hasCollectibles: newCollectibles.length > 0,
      fallbackUsed: collectibleFallbackUsed,
      source: collectibleSourceReason,
    });

    // Play level start sound (deferred when silent=true for loading transitions)
    if (!options?.silent) {
      playLevelStart();
    }

    // Start world-specific background music (deferred when silent=true)
    if (!options?.silent) {
      startWorldMusic(worldDef.id);
    }
    
    // Clear commentary for new level
    const commentarySystem = getCommentarySystem();
    commentarySystem.clear();

    // Calculate actual grid width for this puzzle
    const actualWidth = puzzleGrid[0]?.length || GAME_WIDTH;

    setGameState(prev => {
      // Apply magnet effect on level start if player has magnet prop
      const propManager = getPropEffectsManager();
      const passiveEffects = getPassiveEffects();
      const magnetRange = passiveEffects.find(e => e.type === 'magnet')?.value || 0;

      let processedCollectibles = newCollectibles;
      if (magnetRange > 0) {
        // Apply magnet effect to initial position
        processedCollectibles = propManager.applyEffects(
          passiveEffects,
          { ...prev, collectibles: newCollectibles, position: newInitialPosition }
        ).collectibles || newCollectibles;
      }

      return {
        ...prev,
        level: levelIndex,
        position: [...newInitialPosition],
        initialPosition: [...newInitialPosition],
        end: [...newEnd],
        rocks: newRocks,
        width: actualWidth,
        collectibles: processedCollectibles,
        collectedInLevel: 0,
        isPlaying: true,
        isPaused: false,
        isTutorial,
        enemies,
        moveHistory: [],
        frozenTurns: 0,
        scoutMovesLeft: 0,
        isWarpSelecting: false,
        hand: [],
      };
    });

    // Clear card-granted temporary powerups (shield, invincibility) so they
    // don't persist across level transitions.
    if (shopManager.hasPowerup('shield')) {
      shopManager.usePowerup('shield');
    }

    resetGameStats();
    if (!isTutorial) {
      startTimer(levelIndex);
    }

    // Add level transition animation
    if (effects.animateLevelTransition) {
      effects.animateLevelTransition();
    }
    
    // Check for new obstacles and dispatch event if found
    const newObstacle = checkForNewObstacles(puzzleGrid, levelIndex);
    if (newObstacle) {
      // Wait a bit for level to fully load before showing tutorial
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('newObstacle', { 
          detail: { obstacle: newObstacle }
        }));
      }, 1000);
    }
    
    return true;
  }, [resetGameStats, startTimer, playLevelStart, startWorldMusic, checkForNewObstacles, getPassiveEffects, restartCounter]);

  /**
   * Loads a procedurally generated infinite mode wave.
   * Uses the same state structure as loadLevel but generates the grid on the fly.
   */
  const loadInfiniteWave = useCallback((wave: number, existingInfiniteState?: InfiniteState, options?: { silent?: boolean }) => {
    const seed = existingInfiniteState?.seed ?? Date.now();
    const tierIdx = waveToTier(wave);
    const usedMap = existingInfiniteState?.usedLevelIndices ?? {};
    const usedIndices = new Set(usedMap[tierIdx] ?? []);
    const { level: levelData, levelIndex } = pickInfiniteLevel(wave, seed, usedIndices);

    // Store the generated grid for enemy AI and movement
    currentGridRef.current = levelData.grid;
    teleporterMapRef.current = buildTeleporterMap(levelData.grid);

    // Parse rocks/obstacles from the generated grid
    const newRocks: Array<[number, number, string]> = [];
    levelData.grid.forEach((row, rowIndex) => {
      row.split('').forEach((cell, cellIndex) => {
        switch (cell) {
          case 'x': newRocks.push([cellIndex, rowIndex, 'rock']); break;
          case 'l': newRocks.push([cellIndex, rowIndex, 'lava']); break;
          case 's': newRocks.push([cellIndex, rowIndex, 'stop']); break;
          case 't': newRocks.push([cellIndex, rowIndex, 'teleporter']); break;
        }
      });
    });

    // Build enemy states
    const enemies: EnemyState[] = levelData.enemies.map((e, i) => {
      const config = ENEMY_DIFFICULTY[e.type];
      return {
        id: `enemy_${i}`,
        type: e.type,
        position: e.position,
        previousPosition: e.position,
        direction: deterministicDirection(e.position[0], e.position[1], i),
        isStunned: false,
        turnsUntilActive: computeActivationDelay(e.position, levelData.start, config),
      };
    });

    // Build collectibles
    const collectibles = levelData.collectibles.map((pos, i) => ({
      id: `inf_collect_${wave}_${i}`,
      position: pos,
      type: (i % 3 === 0 ? 'gem' : 'coin') as 'coin' | 'gem',
      value: i % 3 === 0 ? 3 : 1,
      isCollected: false,
    }));

    const infiniteState: InfiniteState = {
      wave,
      highestWave: Math.max(wave, existingInfiniteState?.highestWave ?? 0),
      totalScore: existingInfiniteState?.totalScore ?? 0,
      seed,
      usedLevelIndices: {
        ...usedMap,
        [tierIdx]: [...(usedMap[tierIdx] ?? []), levelIndex],
      },
    };

    // Persist run state so the player can resume after refresh / navigation
    saveInfiniteRunState(infiniteState);

    // Play level start sound (deferred when silent=true for loading transitions)
    if (!options?.silent) {
      playLevelStart();
    }

    // Start music based on wave progression (cycle through worlds)
    const worldIds = ['greenwood', 'stonehollow', 'frostline', 'emberdeep', 'arcana'] as const;
    const worldIndex = Math.floor((wave - 1) / 5) % worldIds.length;
    if (!options?.silent) {
      startWorldMusic(worldIds[worldIndex]);
    }

    // Clear commentary
    const commentarySystem = getCommentarySystem();
    commentarySystem.clear();

    // Show wave info
    commentarySystem.trigger(`Wave ${wave}`, 'excited', 'high');

    setGameState(prev => ({
      ...prev,
      level: -1, // sentinel value for infinite mode
      position: [...levelData.start],
      initialPosition: [...levelData.start],
      end: [...levelData.end],
      rocks: newRocks,
      width: levelData.gridWidth,
      collectibles,
      collectedInLevel: 0,
      isPlaying: true,
      isPaused: false,
      isTutorial: false,
      gameMode: 'endless',
      enemies,
      moveHistory: [],
      frozenTurns: 0,
      scoutMovesLeft: 0,
      isWarpSelecting: false,
      hand: [],
      infiniteState,
      infiniteGrid: levelData.grid,
    }));

    resetGameStats();
    startTimer(-1); // pass -1 for infinite mode

    return true;
  }, [resetGameStats, startTimer, playLevelStart, startWorldMusic]);

  // Movement system exactly matching legacy brain.js movePlayer function
  const movePlayerInDirection = useCallback((axis: number, perpendicularAxis: number, movingForward: boolean) => {
    setGameState(prev => {
      if (!prev.isPlaying || prev.isPaused) return prev;

      // Use stored grid ref (works for both standard and infinite mode)
      const puzzleGrid = currentGridRef.current;
      if (!puzzleGrid || puzzleGrid.length === 0) return prev;
      const passiveEffects = getPassiveEffects();
      const propManager = getPropEffectsManager();

      // Push a snapshot of the current state for move history (used by Rewind card + stalk AI)
      const snapshot: MoveSnapshot = {
        playerPosition: prev.position,
        enemies: prev.enemies.map(e => ({ ...e })),
      };
      const newMoveHistory = [...prev.moveHistory.slice(-2), snapshot]; // Keep last 3

      // Work with mutable position array like legacy
      const position: Position = [prev.position[0], prev.position[1]];
      const rocks = prev.rocks;

      // Exact legacy logic: find relevant rocks
      const allRelevantRocks = rocks
        .filter(rock => rock[axis] === position[axis]) // Filtering rocks in this axis
        .filter(rock =>
          movingForward
            ? (rock[perpendicularAxis] as number) > position[perpendicularAxis]
            : (rock[perpendicularAxis] as number) < position[perpendicularAxis]
        ); // Filtering rocks that are in the desired direction

      // Phase effect: slide through rock-type walls entirely
      const canPhaseThrough = propManager.canPhase(passiveEffects);
      // Bounce effect: skip first N rock-type obstacles
      const bounceValue = passiveEffects.find(e => e.type === 'bounce')?.value || 0;

      let relevantRocks = allRelevantRocks;
      if (canPhaseThrough) {
        relevantRocks = allRelevantRocks.filter(r => r[2] !== 'rock');
      } else if (bounceValue > 0) {
        // Sort by distance, skip first N rocks
        const sorted = [...allRelevantRocks].sort((a, b) =>
          movingForward
            ? (a[perpendicularAxis] as number) - (b[perpendicularAxis] as number)
            : (b[perpendicularAxis] as number) - (a[perpendicularAxis] as number)
        );
        let skipped = 0;
        relevantRocks = sorted.filter(r => {
          if (r[2] === 'rock' && skipped < bounceValue) {
            skipped++;
            return false;
          }
          return true;
        });
      }

      let statsUpdate: Partial<GameStats> = {};
      let shouldPlayLavaSound = false;
      let landedOnObstacle = false; // Track if we landed on a known obstacle tile

      if (relevantRocks.length) {
        // If there are rocks in this path - exact legacy logic
        const minmax = movingForward
          ? Math.min(...relevantRocks.map(rock => rock[perpendicularAxis] as number))
          : Math.max(...relevantRocks.map(rock => rock[perpendicularAxis] as number));
        const relevantRock = relevantRocks.filter(rock => rock[perpendicularAxis] === minmax)[0];

        if (relevantRock[2] === 'rock') {
          position[perpendicularAxis] = movingForward ? minmax - 1 : minmax + 1;
          statsUpdate.rocksEncountered = prev.gameStats.rocksEncountered + 1;
          // Play wall hit sound
          playWallHit();
          // Set collision flag
          (statsUpdate as any).hasCollided = true;
        } else {
          if (relevantRock[2] === 'lava') {
            // Check for invincibility or shield (from powerups or props)
            const hasPropsShield = propManager.hasShieldProtection(passiveEffects);

            if (!hasInvincibility() && !hasShield() && !hasPropsShield) {
              // Don't move to lava, stay at current position
              position[perpendicularAxis] = movingForward ? minmax - 1 : minmax + 1;
              statsUpdate.lavaEncountered = prev.gameStats.lavaEncountered + 1;
              shouldPlayLavaSound = true;
              playFireHit();
              
              // Lose a life and reset to start (use ref to get latest lives count)
              const hasLivesLeft = shopStateRef.current.lives > 1;
              if (hasLivesLeft) {
                // Has lives left - will reset to start position after state update
                statsUpdate.retries = prev.gameStats.retries + 1;
                // Set flag to reset position and lose a life
                (statsUpdate as any).shouldResetPosition = true;
                (statsUpdate as any).shouldLoseLife = true;
              } else {
                // Game over - no lives left (or will be 0 after losing this one)
                (statsUpdate as any).shouldTriggerGameOver = true;
                (statsUpdate as any).shouldLoseLife = true;
              }
            } else if (hasShield() && !hasInvincibility()) {
              // Shield consumed, move to lava position but take no damage
              shopManager.usePowerup('shield');
              position[perpendicularAxis] = minmax;
              shouldPlayLavaSound = true;
              playShieldAbsorb();
            } else {
              // Has invincibility, can walk on lava safely
              position[perpendicularAxis] = minmax;
            }
          } else {
            // Not lava, handle stop tiles / teleporters
            position[perpendicularAxis] = minmax;
            landedOnObstacle = true; // Position is ON a known obstacle tile
            if (relevantRock[2] === 'stop') {
              statsUpdate.stopsEncountered = prev.gameStats.stopsEncountered + 1;
              playIceSlide();
              // Ice sparkle effect for stop tile
              effects.triggerEffect({
                type: 'sparkle',
                position: [50, 50],
                duration: 800,
                color: '#5dade2',
                intensity: 2
              });
            }
          }
        }
      } else {
        // No obstacles - slide to edge of grid
        // Guard against out-of-bounds row access for variable-width grids
        const currentRow = puzzleGrid[position[1]];
        const maxCells = axis === 1
          ? (currentRow ? currentRow.length : puzzleGrid[0]?.length || 1)
          : puzzleGrid.length;
        position[perpendicularAxis] = movingForward ? maxCells - 1 : 0;
      }

      // Clamp position to grid bounds to prevent player leaving the board.
      // Skip re-clamping when we landed on a known obstacle tile (stop/teleporter)
      // because the tile's position is authoritative and clamping to a
      // variable-width row could incorrectly shift the player off the tile.
      if (!landedOnObstacle) {
        const gridRows = puzzleGrid.length;
        const gridCols = puzzleGrid[Math.max(0, Math.min(position[1], gridRows - 1))]?.length || puzzleGrid[0]?.length || 1;
        position[0] = Math.max(0, Math.min(position[0], gridCols - 1));
        position[1] = Math.max(0, Math.min(position[1], gridRows - 1));
        // After row clamping, re-clamp column to the actual row width
        const finalRowWidth = puzzleGrid[position[1]]?.length || 1;
        position[0] = Math.max(0, Math.min(position[0], finalRowWidth - 1));
      }

      // Only count as a step if the position actually changed (pre-teleport).
      const tookStep = position[0] !== prev.position[0] || position[1] !== prev.position[1];
      if (tookStep) {
        statsUpdate.stepsTaken = prev.gameStats.stepsTaken + 1;
      }

      const hasCollisionFeedback = Boolean((statsUpdate as any).hasCollided);
      // If position didn't change, return previous state unchanged (no unnecessary re-renders)
      // unless we still need to surface collision feedback.
      if (!tookStep && !shouldPlayLavaSound && !hasCollisionFeedback) {
        return prev;
      }

      // Teleporter: if we landed on a teleporter tile, jump to its paired location (once per move).
      let teleportedFrom: Position | null = null;
      if (tookStep) {
        const dest = getTeleporterDestination(teleporterMapRef.current, position);
        if (dest) {
          teleportedFrom = [position[0], position[1]];

          // Dispatch teleport event so the visual layer can animate suck-in / grow-out
          window.dispatchEvent(new CustomEvent('teleport', {
            detail: {
              from: [position[0], position[1]],
              to: [dest[0], dest[1]],
            }
          }));

          // Play teleport sound
          playTeleport();

          position[0] = dest[0];
          position[1] = dest[1];

          // Visual feedback
          effects.triggerEffect({
            type: 'sparkle',
            position: [50, 50],
            duration: 700,
            color: '#9C27B0',
            intensity: 3,
          });
          if (effects.createScreenFlash) {
            effects.createScreenFlash('#9C27B0', 0.25);
          }
        }
      }

      // Play sounds and effects immediately like legacy
      if (shouldPlayLavaSound) {
        playHitObstacle();
        // Trigger impact animation and effects
        if (effects.animateCharacterImpact) {
          effects.animateCharacterImpact();
        }
        effects.triggerEffect({
          type: 'explosion',
          position: [50, 50], // Center of screen
          duration: 600,
          color: '#ff6b35'
        });
        // Add screen flash for damage
        if (effects.createScreenFlash) {
          effects.createScreenFlash('#ff6b35', 0.4);
        }
      }

      // Check for collectible collection along the ENTIRE slide path (not just the landing tile).
      let updatedCollectibles = prev.collectibles;
      let collectedValue = 0;
      let playedCollectSound = false;

      const collectAt = (pos: Position) => {
        const hit = updatedCollectibles.find(
          c => !c.isCollected && c.position[0] === pos[0] && c.position[1] === pos[1]
        );
        if (!hit) return;

        updatedCollectibles = updatedCollectibles.map(c =>
          c.id === hit.id ? { ...c, isCollected: true } : c
        );
        // Double coins powerup multiplier
        const coinMultiplier = shopManager.hasPowerup('doubleCoins') ? 2 : 1;
        collectedValue += hit.value * coinMultiplier;

        if (!playedCollectSound) {
          playedCollectSound = true;
          playCollectCoin();
        }

        effects.triggerEffect({
          type: 'coinCollect',
          position: [50, 50],
          duration: 600,
          color: hit.type === 'gem' ? '#9C27B0' : '#FFD700',
        });

        // Dispatch event for floating text at collection point
        window.dispatchEvent(new CustomEvent('coinCollected', {
          detail: {
            position: [pos[0], pos[1]],
            value: hit.value * coinMultiplier,
          }
        }));
      };

      // Collect along every tile the player slid through (start exclusive, end inclusive).
      if (tookStep) {
        const startPos: Position = [prev.position[0], prev.position[1]];
        const slideStopPos: Position = teleportedFrom
          ? [teleportedFrom[0], teleportedFrom[1]]
          : [position[0], position[1]];
        const endPos: Position = [slideStopPos[0], slideStopPos[1]];

        // Determine direction of slide on the perpendicular axis
        if (startPos[0] === endPos[0]) {
          // Vertical slide (same column, row changed)
          const step = endPos[1] > startPos[1] ? 1 : -1;
          for (let r = startPos[1] + step; step > 0 ? r <= endPos[1] : r >= endPos[1]; r += step) {
            collectAt([startPos[0], r]);
          }
        } else {
          // Horizontal slide (same row, column changed)
          const step = endPos[0] > startPos[0] ? 1 : -1;
          for (let c = startPos[0] + step; step > 0 ? c <= endPos[0] : c >= endPos[0]; c += step) {
            collectAt([c, startPos[1]]);
          }
        }
      }

      // And at teleporter destination (already included in the path above if no teleport,
      // but needed for the destination side of a teleport)
      if (teleportedFrom) collectAt(position);
      
      // Apply magnet effect to collect nearby coins
      const magnetEffect = passiveEffects.find(e => e.type === 'magnet');
      
      if (magnetEffect) {
        const beforeCollectedIds = new Set(updatedCollectibles.filter(c => c.isCollected).map(c => c.id));
        updatedCollectibles = propManager.applyEffects(
          passiveEffects,
          { ...prev, collectibles: updatedCollectibles, position }
        ).collectibles || updatedCollectibles;

        const newlyCollectedByMagnet = updatedCollectibles.filter(
          (c) => c.isCollected && !beforeCollectedIds.has(c.id)
        );
        for (const c of newlyCollectedByMagnet) {
          collectedValue += c.value;
          effects.triggerEffect({
            type: 'coinCollect',
            position: [50, 50],
            duration: 300,
            color: c.type === 'gem' ? '#9C27B0' : '#FFD700',
          });
        }
      }

      // If the player reached the exit tile with all collectibles, skip enemy collision
      // -- the player escapes and the level completes.
      const reachedExit =
        position[0] === prev.end[0] && position[1] === prev.end[1];
      const allCollected =
        updatedCollectibles.length === 0 ||
        updatedCollectibles.every(c => c.isCollected);

      // Check if player slid into an enemy
      let updatedEnemies = prev.enemies;
      const collidedEnemy =
        reachedExit && allCollected
          ? null // Player reaches exit -- ignore enemy on that tile
          : prev.enemies.find(
              e => e.position[0] === position[0] && e.position[1] === position[1],
            );

      if (collidedEnemy) {
        const hasPropsShield = propManager.hasShieldProtection(passiveEffects);
        if (hasShield() || hasPropsShield) {
          // Shield absorbs the hit - stun the enemy, consume shield
          updatedEnemies = prev.enemies.map(e =>
            e.id === collidedEnemy.id ? { ...e, isStunned: true } : e
          );
          // Consume one shield charge
          if (hasShield()) {
            shopManager.usePowerup('shield');
          }
          // Stay at previous position (before collision)
          position[0] = prev.position[0];
          position[1] = prev.position[1];
          (statsUpdate as any).hasCollided = true;
          playShieldAbsorb();
        } else if (!hasInvincibility()) {
          playEnemyCollision();
          playHurt();
          // No protection - lose life, reset position
          const hasLivesLeft = shopStateRef.current.lives > 1;
          if (hasLivesLeft) {
            statsUpdate.retries = prev.gameStats.retries + 1;
            (statsUpdate as any).shouldResetPosition = true;
            (statsUpdate as any).shouldLoseLife = true;
          } else {
            (statsUpdate as any).shouldTriggerGameOver = true;
            (statsUpdate as any).shouldLoseLife = true;
          }
          (statsUpdate as any).hasCollided = true;
          // Stay at previous position
          position[0] = prev.position[0];
          position[1] = prev.position[1];
        }
        // If invincible, just slide through - stun the enemy
        if (hasInvincibility()) {
          updatedEnemies = prev.enemies.map(e =>
            e.id === collidedEnemy.id ? { ...e, isStunned: true } : e
          );
        }
      }

      const hasCollided = Boolean((statsUpdate as any).hasCollided);
      const shouldLoseLife = Boolean((statsUpdate as any).shouldLoseLife);
      const shouldResetPosition = Boolean((statsUpdate as any).shouldResetPosition);
      const shouldTriggerGameOver = Boolean((statsUpdate as any).shouldTriggerGameOver);

      if (hasCollided) {
        delete (statsUpdate as any).hasCollided;
      }
      if (shouldLoseLife) {
        delete (statsUpdate as any).shouldLoseLife;
      }
      if (shouldResetPosition) {
        delete (statsUpdate as any).shouldResetPosition;
      }
      if (shouldTriggerGameOver) {
        delete (statsUpdate as any).shouldTriggerGameOver;
      }

      // Decrement scout moves counter when the player actually moved
      const newScoutMoves = tookStep && prev.scoutMovesLeft > 0
        ? prev.scoutMovesLeft - 1
        : prev.scoutMovesLeft;

      // Handle reset/game over flags
      const newState: GameState = {
        ...prev,
        position,
        gameStats: { ...prev.gameStats, ...statsUpdate },
        collectibles: updatedCollectibles,
        collectedInLevel: prev.collectedInLevel + collectedValue,
        hasCollided,
        enemies: updatedEnemies,
        moveHistory: newMoveHistory,
        scoutMovesLeft: newScoutMoves,
      };

      // Deduct life on collision
      if (shouldLoseLife) {
        shopManager.loseLife();
      }

      // Check for reset flag (life lost but still has lives)
      if (shouldResetPosition) {
        playLevelFailed();
        // Dispatch lifeLost event so GameBoard can play death animation
        window.dispatchEvent(new CustomEvent('lifeLost'));
        // Reset to initial position after death animation completes (~1.3s)
        if (deathTimerRef.current) clearTimeout(deathTimerRef.current);
        deathTimerRef.current = setTimeout(() => {
          deathTimerRef.current = null;
          setGameState(prevState => ({
            ...prevState,
            position: [...prevState.initialPosition] as Position,
          }));
          // Dispatch resetAfterDeath so GameBoard can re-enter
          window.dispatchEvent(new CustomEvent('resetAfterDeath'));
        }, 1400);
      }

      // Check for game over flag (last life lost)
      if (shouldTriggerGameOver) {
        playLevelFailed();
        // Dispatch lifeLost first for death animation
        window.dispatchEvent(new CustomEvent('lifeLost'));
        // Then game over after death animation plays
        if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
        gameOverTimerRef.current = setTimeout(() => {
          gameOverTimerRef.current = null;
          setGameState(prevState => ({
            ...prevState,
            isPlaying: false
          }));
          window.dispatchEvent(new CustomEvent('gameOver', {
            detail: {
              level: prev.level,
              score: prev.gameStats.score,
              steps: prev.gameStats.stepsTaken
            }
          }));
        }, 1400);
      }

      // Schedule collision state clearing if collision was set
      if (hasCollided) {
        // Clear any existing collision timer
        if (collisionTimerRef.current) {
          clearTimeout(collisionTimerRef.current);
        }
        collisionTimerRef.current = setTimeout(() => {
          setGameState(prevState => ({ ...prevState, hasCollided: false }));
        }, 300);
      }

      return newState;
    });
  }, [playHitObstacle, playWallHit, playIceSlide, playFireHit, handleLevelComplete, levelCompleteTriggered]);

  // Check if movement is valid before playing effects
  const canMove = useCallback((direction: Direction): boolean => {
    const { level, position, rocks } = gameState;
    // In infinite mode, use the stored grid; otherwise look up from puzzles[]
    const isInfinite = gameState.gameMode === 'endless' && gameState.infiniteGrid;
    const puzzle = isInfinite ? null : (puzzles[level] as PuzzleData);
    if (!puzzle && !isInfinite) return false;

    const puzzleGrid = isInfinite ? gameState.infiniteGrid! : getPuzzleGrid(puzzle!);

    // Safety: verify position is within grid bounds
    if (position[1] < 0 || position[1] >= puzzleGrid.length) return false;
    const currentRow = puzzleGrid[position[1]];
    if (!currentRow || position[0] < 0 || position[0] >= currentRow.length) return false;

    // Determine the axis and direction
    let axis: number, perpendicularAxis: number, movingForward: boolean;
    switch (direction) {
      case 'up':
        axis = 0;
        perpendicularAxis = 1;
        movingForward = false;
        break;
      case 'down':
        axis = 0;
        perpendicularAxis = 1;
        movingForward = true;
        break;
      case 'left':
        axis = 1;
        perpendicularAxis = 0;
        movingForward = false;
        break;
      case 'right':
        axis = 1;
        perpendicularAxis = 0;
        movingForward = true;
        break;
    }

    // Check if at boundary
    const currentPos = position[perpendicularAxis];
    const maxPos = axis === 1 ? currentRow.length - 1 : puzzleGrid.length - 1;

    if (movingForward && currentPos >= maxPos) return false;
    if (!movingForward && currentPos <= 0) return false;

    // Check for immediate rock blocking
    const immediateRocks = rocks.filter(rock =>
      rock[axis] === position[axis] &&
      rock[perpendicularAxis] === (movingForward ? currentPos + 1 : currentPos - 1)
    );

    if (immediateRocks.some(rock => rock[2] === 'rock')) {
      return false;
    }

    return true;
  }, [gameState]);

  // Movement functions exactly like legacy - direct and fast
  const movePlayer = useCallback((direction: Direction) => {
    if (gameState.isPaused) return;

    // Update last movement direction
    setGameState(prev => ({ ...prev, lastMovementDirection: direction }));
    
    // Only play sound and animation if movement is valid
    if (canMove(direction)) {
      playMove();
      
      // Trigger character movement animation
      if (effects.animateCharacterMove) {
        effects.animateCharacterMove(direction);
      }
    }

    switch (direction) {
      case 'up':
        movePlayerInDirection(0, 1, false);
        break;
      case 'down':
        movePlayerInDirection(0, 1, true);
        break;
      case 'left':
        movePlayerInDirection(1, 0, false);
        break;
      case 'right':
        movePlayerInDirection(1, 0, true);
        break;
    }
  }, [movePlayerInDirection, playMove, canMove, effects, gameState.isPaused]);

  // Restart level with full reset and intro animations
  const restartLevel = useCallback(() => {
    // Reset completion flags to allow level restart
    setLevelCompleteTriggered(false);
    setPendingLevelComplete(null);

    // Stop current music and timer
    stopTimer();
    stopWorldMusic();

    // Get current retry count before reloading
    const currentRetries = gameState.gameStats.retries;

    // Force stop the game to trigger full restart
    setGameState(prev => ({ ...prev, isPlaying: false }));

    // Increment restart counter to force camera reset
    setRestartCounter(prev => prev + 1);

    // Small delay then fully reload the current level (this resets collectibles, triggers camera/character animations)
    setTimeout(() => {
      let success: boolean | undefined;
      if (gameState.gameMode === 'endless' && gameState.infiniteState) {
        // Infinite mode: regenerate the same wave
        success = loadInfiniteWave(gameState.infiniteState.wave, gameState.infiniteState);
      } else {
        success = loadLevel(gameState.level);
      }

      if (success) {
        // Update retry count after level loads
        setTimeout(() => {
          setGameState(prev => ({
            ...prev,
            gameStats: {
              ...prev.gameStats,
              retries: currentRetries + 1,
            }
          }));
        }, 100);
      }
    }, 100);
  }, [gameState.level, gameState.gameMode, gameState.infiniteState, gameState.gameStats.retries, loadLevel, loadInfiniteWave, stopTimer, stopWorldMusic]);

  // Go to home screen
  const goToHomeScreen = useCallback(() => {
    stopTimer();
    stopWorldMusic();
    setGameState(prev => ({
      ...prev,
      level: 0,
      isPlaying: false,
      isPaused: false,
    }));
  }, [stopTimer, stopWorldMusic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (collisionTimerRef.current) {
        clearTimeout(collisionTimerRef.current);
      }
      // Clear any in-flight enemy stagger timeouts
      enemyStaggerTimeoutsRef.current.forEach((tid) => clearTimeout(tid));
      enemyStaggerTimeoutsRef.current = [];
      // Clear death/game-over timers
      if (deathTimerRef.current) clearTimeout(deathTimerRef.current);
      if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
    };
  }, []);

  // Reset level complete flag when level changes or restarts
  useEffect(() => {
    setLevelCompleteTriggered(false);
    levelCompleteTriggeredRef.current = false;
    setPendingLevelComplete(null);
  }, [gameState.level, restartCounter]);

  // Load next level (called after modal is closed).
  // When silent=true, skips transition sound and passes silent to loadLevel/loadInfiniteWave
  // so the caller (Game3DScreen) can handle sounds after a visual transition.
  const loadNextLevel = useCallback((options?: { silent?: boolean }) => {
    // Infinite mode: advance to next wave
    if (gameState.gameMode === 'endless' && gameState.infiniteState) {
      setLevelCompleteTriggered(false);
      setPendingLevelComplete(null);
      if (!options?.silent) playLevelTransition();

      const nextWave = gameState.infiniteState.wave + 1;
      const updatedInfiniteState: InfiniteState = {
        ...gameState.infiniteState,
        wave: nextWave,
        highestWave: Math.max(nextWave, gameState.infiniteState.highestWave),
        totalScore: gameState.infiniteState.totalScore + gameState.gameStats.score,
      };

      // Persist highest wave
      const stored = parseInt(localStorage.getItem('infiniteHighestWave') || '0');
      if (updatedInfiniteState.highestWave > stored) {
        localStorage.setItem('infiniteHighestWave', updatedInfiniteState.highestWave.toString());
      }

      loadInfiniteWave(nextWave, updatedInfiniteState, options);
      return;
    }

    // Standard mode
    const nextLevel = gameState.level + 1;
    if (nextLevel < puzzles.length) {
      // Reset completion flags
      setLevelCompleteTriggered(false);
      setPendingLevelComplete(null);

      if (!options?.silent) playLevelTransition();

      // Update URL to reflect the new level
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('level', nextLevel.toString());
      window.history.pushState({}, '', newUrl.toString());

      loadLevel(nextLevel, options);
    } else {
      // All levels completed, dispatch event to go home
      window.dispatchEvent(new CustomEvent('allLevelsComplete'));
    }
  }, [gameState.level, gameState.gameMode, gameState.infiniteState, gameState.gameStats.score, loadLevel, loadInfiniteWave, playLevelTransition]);

  // Warp to a specific position (used by warp card tile selection)
  const warpToPosition = useCallback((pos: Position) => {
    setGameState(prev => {
      if (!prev.isWarpSelecting) return prev;

      // Dispatch teleport event for visual feedback
      window.dispatchEvent(new CustomEvent('teleport', {
        detail: {
          from: [prev.position[0], prev.position[1]],
          to: [pos[0], pos[1]],
        }
      }));

      return {
        ...prev,
        position: [pos[0], pos[1]] as Position,
        isWarpSelecting: false,
      };
    });
    playTeleport();
  }, [playTeleport]);

  // Play a card effect, applying its game-state changes.
  // Shield: grants a shield powerup via the shop system.
  // Freeze: freezes all enemies for 2 turns.
  // Scout: shows the optimal path for 3 moves (leverages hint path system).
  // Rewind: restores the oldest move-history snapshot.
  // Warp: enables tile-selection mode for teleporting.
  const playCardEffect = useCallback((cardType: CardType) => {
    switch (cardType) {
      case 'freeze':
        // Freeze all enemies for 2 turns
        playFreeze();
        setGameState(prev => ({ ...prev, frozenTurns: prev.frozenTurns + 2 }));
        break;

      case 'shield':
        // Grant a temporary shield via the shop powerup system.
        // shopManager.addPowerup increments the shield count so that
        // hasShield() returns true and collision handling absorbs the hit.
        shopManager.addPowerup('shield');
        break;

      case 'scout':
        // Show the optimal path for the next 3 moves.
        // The hint path useEffect already calculates the path when
        // shopState.powerups.hint > 0 OR scoutMovesLeft > 0.
        setGameState(prev => ({ ...prev, scoutMovesLeft: prev.scoutMovesLeft + 3 }));
        break;

      case 'rewind':
        // Undo the last 3 moves by restoring the oldest snapshot.
        setGameState(prev => {
          if (prev.moveHistory.length === 0) return prev;
          const oldest = prev.moveHistory[0];
          return {
            ...prev,
            position: oldest.playerPosition,
            enemies: oldest.enemies,
            moveHistory: [],
          };
        });
        break;

      case 'warp':
        // Enable warp selection mode - player taps a tile to teleport.
        playWarp();
        setGameState(prev => ({ ...prev, isWarpSelecting: true }));
        break;
    }
  }, [shopManager, playFreeze, playWarp]);

  return {
    gameState,
    timer,
    complexity,
    loadLevel,
    loadInfiniteWave,
    loadNextLevel,
    movePlayer,
    restartLevel,
    goToHomeScreen,
    updateSettings,
    startTimer,
    stopTimer,
    resetTimer,
    hintPath,
    hintPathHazards,
    triggerLevelCompleteModal,
    restartCounter, // Expose restart counter for camera system
    togglePause,
    setPaused,
    playCardEffect,
    warpToPosition,
  };
};
